import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import SensibleWeatherWrapper from '../components/SensibleWeatherWrapper';
import { getSelectedEvent } from '../../../selectors/event';
import { toDecimals } from '@h/currency';
import { setSensibleWeatherQuote } from 'checkout/views/insurance/actions/sensible-weather';

class WeatherInsuranceContainer extends Component {
  render() {
    return (
      this.props.showSensibleWeather && (
        <SensibleWeatherWrapper
          selected={true}
          coverageStartDate={this.props.coverageStartDate}
          coverageEndDate={this.props.coverageEndDate}
          currency={this.props.currency}
          exposureName={this.props.exposureName}
          exposureLatitude={this.props.exposureLatitude}
          exposureLongitude={this.props.exposureLongitude}
          exposureTotalCoverageAmount={this.props.exposureTotalCoverageAmount}
          langLocale={this.props.langLocale}
          onInsuranceChange={this.onInsuranceChange}
          isEligible={this.props.isEligible}
          eventId={this.props.eventId}
          coverageStartHourNumber={this.props.coverageStartHourNumber}
          coverageEndHourNumber={this.props.coverageEndHourNumber}
          sensibleExternalId={this.props.sensibleExternalId}
          productId={this.props.productId}
          partnerId={this.props.partnerId}
          sandbox={this.props.sandbox}
        />
      )
    );
  }

  onInsuranceChange = (val) => {
    this.props.setSensibleWeatherQuote(val);
  };
}

const ONE_HOUR = 60 * 60 * 1000;

const calculateEndDate = (date, defaultDuration) => {
  let newEndDate = new Date(date.getTime() + defaultDuration * ONE_HOUR);

  if (newEndDate.getDate() !== date.getDate()) {
    return date;
  }

  if (newEndDate.getDate() > date.getDate()) {
    return newEndDate;
  } else {
    return new Date(date.getTime() + defaultDuration * ONE_HOUR);
  }
};

/**
 * Determines the appropriate coverage end hour based on the endDate, defaultDuration, and startDate.
 *
 * - If endDate is before sensibleWeatherEndDate (8 PM), then return endDate.
 * - If the newEndDate (calculated as endDate + defaultDuration) is after sensibleWeatherEndDate
 *   but before midnight, and on the same date as startDate, then return newEndDate.
 * - If the newEndDate is after or equal to midnight, or if it's not on the same date as startDate,
 *   then return 11 PM of the endDate.
 *
 * @param {Date} endDate - The original end date of the coverage.
 * @param {number} defaultDuration - The duration in hours to add to the endDate.
 * @param {Date} startDate - The start date of the coverage.
 * @returns {Date} - The calculated coverage end hour based on the given logic.
 */
const calculateCoverageEndHour = (endDate, defaultDuration, startDate) => {
  const sensibleWeatherEndDate = new Date(endDate);
  sensibleWeatherEndDate.setHours(20, 0, 0, 0); // 8pm

  // Check if the endDate is not on the same date as startDate
  const isSameDate = endDate.toDateString() === startDate.toDateString();

  if (endDate < sensibleWeatherEndDate && isSameDate) {
    return endDate;
  }

  const midnight = new Date(endDate);
  midnight.setHours(0, 0, 0, 0);
  midnight.setDate(midnight.getDate() + 1); // Midnight of the next day

  const newEndDate = new Date(endDate.getTime() + defaultDuration * ONE_HOUR);

  if (newEndDate > sensibleWeatherEndDate && newEndDate < midnight && isSameDate) {
    return newEndDate;
  }

  if (newEndDate >= midnight || !isSameDate) {
    const elevenPM = new Date(midnight);
    elevenPM.setHours(23, 0, 0, 0); // 11pm of the same day
    return elevenPM;
  }

  return new Date(Math.ceil(newEndDate.getTime() / ONE_HOUR) * ONE_HOUR);
};

const formatDateInTimeZone = (date) => {
  date = new Date(+date);
  date.setTime(date.getTime() - date.getTimezoneOffset() * 60000);
  return date.toISOString().slice(0, 10);
};

const mapStateToProps = (state) => {
  const swDefaultDurationHour = state.settings.sensibleWeatherDuration;
  const venue = state.venue.venues ? Object.values(state.venue.venues)[0] : null;

  const tbdVenueName = 'TBD VENUE';
  const isEligible = venue.name.toLowerCase() !== tbdVenueName.toLowerCase();
  const selectedEvent = getSelectedEvent(state);
  const date = new Date(selectedEvent.event.start);

  let endDate = selectedEvent.event.end
    ? new Date(selectedEvent.event.end)
    : calculateEndDate(new Date(selectedEvent.event.start), swDefaultDurationHour);

  // check if the endDate is the same day as date and if not set it to date 23:00
  if (endDate.getDate() !== date.getDate()) {
    endDate = new Date(date);
    endDate.setHours(23, 0, 0, 0);
  }

  const coverageStartDate = formatDateInTimeZone(date);
  const coverageEndDate = formatDateInTimeZone(endDate);
  const totalCost = state.reservation.total_cost;
  const coverageStartHourNumber = date.getHours();

  const coverageEndHourNumber = calculateCoverageEndHour(endDate, swDefaultDurationHour, date).getHours();

  return {
    currency: state.reservation.currency,
    coverageStartDate: coverageStartDate,
    coverageEndDate: coverageEndDate,
    exposureName: selectedEvent.event.name,
    exposureLatitude: parseFloat(venue.address.latitude),
    exposureLongitude: parseFloat(venue.address.longitude),
    exposureTotalCoverageAmount: toDecimals(totalCost),
    langLocale: 'en-US',
    showSensibleWeather: selectedEvent.show_weather,
    isEligible: isEligible,
    eventId: selectedEvent.event.event_id,
    coverageStartHourNumber: coverageStartHourNumber,
    coverageEndHourNumber: coverageEndHourNumber,
    sensibleExternalId: state.weather.sensible_external_id,
    productId: state.weather.product_id,
    partnerId: state.weather.partner_id,
    sandbox: state.weather.sandbox,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      setSensibleWeatherQuote,
    },
    dispatch,
  );
};

WeatherInsuranceContainer.propTypes = {
  coverageStartDate: PropTypes.string.isRequired,
  coverageEndDate: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
  exposureName: PropTypes.string.isRequired,
  exposureLatitude: PropTypes.number.isRequired,
  exposureLongitude: PropTypes.number.isRequired,
  exposureTotalCoverageAmount: PropTypes.number.isRequired,
  langLocale: PropTypes.string.isRequired,
  showSensibleWeather: PropTypes.bool.isRequired,
  isEligible: PropTypes.bool.isRequired,
  eventId: PropTypes.number.isRequired,
  coverageStartHourNumber: PropTypes.number,
  coverageEndHourNumber: PropTypes.number,
  sensibleExternalId: PropTypes.string,
  productId: PropTypes.string,
  partnerId: PropTypes.string,
  sandbox: PropTypes.bool,
};

WeatherInsuranceContainer.defaultProps = {
  langLocale: 'en-US',
};

export default connect(mapStateToProps, mapDispatchToProps)(WeatherInsuranceContainer);
